interface ChannelState {
  value: number;
  lastUpdateMs: number;
}

/**
 * One state per channel per peer. The three smoothers used to share one record, so a bearing update right after a
 * distance update saw zero elapsed time and a UWB range seeded the GPS filter (2026-10-16).
 */
const uwbDistanceStates = new Map<string, ChannelState>();
const gpsDistanceStates = new Map<string, ChannelState>();
const bearingStates = new Map<string, ChannelState>();

/** Time-aware EMA: same inputs at different cadences converge to the same smoothed value. */
function timeAwareAlpha(baseAlpha: number, elapsedMs: number, referenceMs = 500): number {
  // A second read in the same instant (two callers resolving one frame) must not move the value again.
  if (!(elapsedMs > 0)) {
    return 0;
  }
  return 1 - (1 - baseAlpha) ** (elapsedMs / referenceMs);
}

function distanceAlpha(rawMeters: number): number {
  if (rawMeters < 3) {
    return 0.7;
  }
  if (rawMeters < 10) {
    return 0.5;
  }
  if (rawMeters < 30) {
    return 0.35;
  }
  if (rawMeters < 60) {
    return 0.25;
  }
  return 0.18;
}

const BEARING_BASE_ALPHA = 0.25;

const UWB_DISTANCE_BASE_ALPHA = 0.85;

/** A filter left this long is restarted from the next reading instead of easing from a stale value. */
const SMOOTHING_RESET_AFTER_MS = 30_000;

function stepLinear(
  states: Map<string, ChannelState>,
  peerId: string,
  raw: number,
  now: number,
  alphaFor: (elapsedMs: number) => number,
): number {
  const state = states.get(peerId);
  if (!state || now - state.lastUpdateMs > SMOOTHING_RESET_AFTER_MS || now < state.lastUpdateMs) {
    states.set(peerId, { value: raw, lastUpdateMs: now });
    return raw;
  }
  const alpha = alphaFor(now - state.lastUpdateMs);
  state.value += alpha * (raw - state.value);
  state.lastUpdateMs = now;
  return state.value;
}

export function smoothUwbDistance(peerId: string, rawMeters: number, now = Date.now()): number {
  return stepLinear(uwbDistanceStates, peerId, rawMeters, now, (elapsed) =>
    timeAwareAlpha(UWB_DISTANCE_BASE_ALPHA, elapsed, 200),
  );
}

export function smoothDistance(peerId: string, rawMeters: number, now = Date.now()): number {
  return stepLinear(gpsDistanceStates, peerId, rawMeters, now, (elapsed) =>
    timeAwareAlpha(distanceAlpha(rawMeters), elapsed),
  );
}

export function smoothBearing(peerId: string, rawDegrees: number, now = Date.now()): number {
  const raw = ((rawDegrees % 360) + 360) % 360;
  const state = bearingStates.get(peerId);
  if (!state || now - state.lastUpdateMs > SMOOTHING_RESET_AFTER_MS || now < state.lastUpdateMs) {
    bearingStates.set(peerId, { value: raw, lastUpdateMs: now });
    return raw;
  }
  const alpha = timeAwareAlpha(BEARING_BASE_ALPHA, now - state.lastUpdateMs);
  // Shortest way round: 350° → 10° turns 20°, not 340°.
  const delta = ((raw - state.value + 540) % 360) - 180;
  state.value = (state.value + alpha * delta + 360) % 360;
  state.lastUpdateMs = now;
  return state.value;
}

export function pruneSmoothingState(activePeerIds: Set<string>): void {
  for (const states of [uwbDistanceStates, gpsDistanceStates, bearingStates]) {
    for (const id of states.keys()) {
      if (!activePeerIds.has(id)) {
        states.delete(id);
      }
    }
  }
}

/** Test helper — reset all smoothing state. */
export function resetSmoothingState(): void {
  uwbDistanceStates.clear();
  gpsDistanceStates.clear();
  bearingStates.clear();
}
